import type { EntityFilesPopoverRow } from './entityFilesPopover.types';
import { PROJECT_FILE_TYPE_VALUES, type ProjectFileTypeValue } from './projectFileTypeConstants';
import { PROJECT_FILES_TYPE_FILTER_STORAGE_KEY } from './projectFilesPopoverConstants';
import type { ProjectFileDraftItem } from './ProjectFilesPopover.types';

export interface ProjectFilesPopoverTypeFilterState {
  types: Record<ProjectFileTypeValue, boolean>;
  untyped: boolean;
}

/** localStorage — как в таблице проектов; sessionOnly — изолированное состояние (модалка фонотеки). */
export type ProjectFilesTypeFilterPersistence = 'localStorage' | 'sessionOnly';

interface StoredV1 {
  v: 1;
  types?: Partial<Record<string, boolean>>;
  untyped?: boolean;
}

export function createDefaultProjectFilesTypeFilterState(): ProjectFilesPopoverTypeFilterState {
  const types = {} as Record<ProjectFileTypeValue, boolean>;
  for (const t of PROJECT_FILE_TYPE_VALUES) {
    types[t] = false;
  }
  return { types, untyped: false };
}

export function parseProjectFilesTypeFilterFromStorage(raw: string | null): ProjectFilesPopoverTypeFilterState {
  const defaults = createDefaultProjectFilesTypeFilterState();
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return defaults;
    const o = parsed as StoredV1;
    if (o.v !== 1) return defaults;
    const types = { ...defaults.types };
    if (o.types && typeof o.types === 'object') {
      for (const t of PROJECT_FILE_TYPE_VALUES) {
        if (typeof o.types[t] === 'boolean') types[t] = o.types[t];
      }
    }
    const untyped = typeof o.untyped === 'boolean' ? o.untyped : defaults.untyped;
    return { types, untyped };
  } catch {
    return defaults;
  }
}

export function serializeProjectFilesTypeFilterState(state: ProjectFilesPopoverTypeFilterState): string {
  return JSON.stringify({ v: 1, types: state.types, untyped: state.untyped });
}

export function loadProjectFilesTypeFilterFromStorage(): ProjectFilesPopoverTypeFilterState {
  if (typeof window === 'undefined') return createDefaultProjectFilesTypeFilterState();
  return parseProjectFilesTypeFilterFromStorage(localStorage.getItem(PROJECT_FILES_TYPE_FILTER_STORAGE_KEY));
}

export function saveProjectFilesTypeFilterToStorage(state: ProjectFilesPopoverTypeFilterState): void {
  try {
    localStorage.setItem(PROJECT_FILES_TYPE_FILTER_STORAGE_KEY, serializeProjectFilesTypeFilterState(state));
  } catch {
    /* quota */
  }
}

export function isProjectFilesTypeFilterActive(state: ProjectFilesPopoverTypeFilterState): boolean {
  if (state.untyped) return true;
  return PROJECT_FILE_TYPE_VALUES.some((t) => state.types[t]);
}

export function entityFileMatchesProjectFilesTypeFilter(
  row: EntityFilesPopoverRow,
  state: ProjectFilesPopoverTypeFilterState
): boolean {
  if (!isProjectFilesTypeFilterActive(state)) return true;

  const raw = row.file_type?.trim() ?? '';
  if (raw === '') {
    return state.untyped;
  }
  if ((PROJECT_FILE_TYPE_VALUES as readonly string[]).includes(raw)) {
    return state.types[raw as ProjectFileTypeValue];
  }
  return false;
}

export function filterEntityFilesByProjectFilesTypeFilter<T extends EntityFilesPopoverRow>(
  files: T[],
  state: ProjectFilesPopoverTypeFilterState
): T[] {
  if (!isProjectFilesTypeFilterActive(state)) return files;
  return files.filter((f) => entityFileMatchesProjectFilesTypeFilter(f, state));
}

function draftItemMatchesProjectFilesTypeFilter(
  entry: ProjectFileDraftItem,
  state: ProjectFilesPopoverTypeFilterState
): boolean {
  const raw = entry.file_type?.trim() ?? '';
  if (raw === '') {
    return state.untyped;
  }
  if ((PROJECT_FILE_TYPE_VALUES as readonly string[]).includes(raw)) {
    return state.types[raw as ProjectFileTypeValue];
  }
  return false;
}

/** Локальный черновик: фильтр по типу так же, как у файлов проекта. */
export function filterLocalDraftFilesByProjectFilesTypeFilter(
  files: ProjectFileDraftItem[],
  state: ProjectFilesPopoverTypeFilterState
): ProjectFileDraftItem[] {
  if (!isProjectFilesTypeFilterActive(state)) return files;
  return files.filter((f) => draftItemMatchesProjectFilesTypeFilter(f, state));
}
